import type { IntlShape } from '@atlassian/jira-intl';
import messages from './messages.tsx';

export type TimeInfo = {
	isBeforeNow: boolean;
	minutes: number | undefined;
	hours: number | undefined;
	days: number | undefined;
	weeks: number | undefined;
	isValid: boolean;
};
const periodRegexp =
	/^(?!$)(?<sign>(^-))?((?<weeks>\d+)w)? ?((?<days>\d+)d)? ?((?<hours>\d+)h)? ?((?<minutes>\d+)m)*$/;

export const IS_YYYY_MM_DD_REGEX = /[1-9][0-9]{3}([-][0-9]{2}){2}/;

/**
 * Check edge case not catched in the regex when the timeData is not separate by spaces (eg: '4w5d' or '4w 5d4h 1m')
 */
export const checkTimeDataEdgeCases = (timeString: string): boolean => {
	const weeksIndex = timeString.indexOf('w');
	const daysIndex = timeString.indexOf('d');
	const hoursIndex = timeString.indexOf('h');
	const minutesIndex = timeString.indexOf('m');
	if (weeksIndex > -1 && weeksIndex < timeString.length - 1 && timeString[weeksIndex + 1] !== ' ') {
		return false;
	}
	if (daysIndex > -1 && daysIndex < timeString.length - 1 && timeString[daysIndex + 1] !== ' ') {
		return false;
	}
	if (hoursIndex > -1 && hoursIndex < timeString.length - 1 && timeString[hoursIndex + 1] !== ' ') {
		return false;
	}
	if (
		minutesIndex > -1 &&
		minutesIndex < timeString.length - 1 &&
		timeString[minutesIndex + 1] !== ' '
	) {
		return false;
	}

	return true;
};

export const getTimeInfo = (timeData?: string): TimeInfo => {
	if (timeData !== undefined) {
		const matches = timeData.match(periodRegexp);
		if (
			periodRegexp.test(timeData) &&
			checkTimeDataEdgeCases(timeData) &&
			matches &&
			matches.groups
		) {
			const { sign = '', weeks = '', days = '', hours = '', minutes = '' } = matches.groups;
			const parsedWeeks = parseInt(weeks, 10);
			const parsedDays = parseInt(days, 10);
			const parsedHours = parseInt(hours, 10);
			const parsedMinute = parseInt(minutes, 10);

			return {
				isBeforeNow: sign === '-',
				minutes: Number.isNaN(parsedMinute) ? undefined : +parsedMinute,
				hours: Number.isNaN(parsedHours) ? undefined : +parsedHours,
				days: Number.isNaN(parsedDays) ? undefined : +parsedDays,
				weeks: Number.isNaN(parsedWeeks) ? undefined : +parsedWeeks,
				isValid: true,
			};
		}
	}
	return {
		isBeforeNow: false,
		minutes: undefined,
		hours: undefined,
		days: undefined,
		weeks: undefined,
		isValid: false,
	};
};

export const getTimeString = (intl: IntlShape, timeInfo: TimeInfo): string =>
	[
		timeInfo.weeks !== undefined
			? intl.formatMessage(messages.valueTimeInWeeks, {
					timeNumber: timeInfo.weeks,
				})
			: false,
		timeInfo.days !== undefined
			? intl.formatMessage(messages.valueTimeInDays, {
					timeNumber: timeInfo.days,
				})
			: false,
		timeInfo.hours !== undefined
			? intl.formatMessage(messages.valueTimeInHours, {
					timeNumber: timeInfo.hours,
				})
			: false,
		timeInfo.minutes !== undefined
			? intl.formatMessage(messages.valueTimeInMinutes, {
					timeNumber: timeInfo.minutes,
				})
			: false,
	]
		.filter(Boolean)
		.join(', ');

export const getTimeInMinutes = (timeInfo: TimeInfo): number => {
	if (timeInfo.isValid) {
		return (
			(timeInfo.isBeforeNow ? -1 : 1) *
			((timeInfo.minutes !== undefined ? timeInfo.minutes : 0) +
				(timeInfo.hours !== undefined ? timeInfo.hours * 60 : 0) +
				(timeInfo.days !== undefined ? timeInfo.days * 24 * 60 : 0) +
				(timeInfo.weeks !== undefined ? timeInfo.weeks * 7 * 24 * 60 : 0))
		);
	}
	return 0;
};

const isNumeric = (value: string) => /^\d+$/.test(value);

export const isValidTimePeriodFormat = (timeDigits?: string, timeUnit?: string): boolean =>
	timeDigits === '' ||
	(timeDigits !== undefined &&
		isNumeric(timeDigits) &&
		parseInt(timeDigits, 10) > -1 &&
		['m', 'h', 'd', 'w'].includes(timeUnit ?? ''));
